import React, {useEffect, useState} from "react";
import {PageTitle} from "../../../page/features/PageTitle";
import {useAppDispatch, useAppSelector} from "../../../../hooks/useTypedSelector";
import {getProduct} from "../../infrastructure/slices/productSlice/productSlice";
import {Badge, Col, Form, Row} from "react-bootstrap";
import {DefaultPage} from "../../../page/features/default";
import {ProductTabs} from "./product/productTabs";
import {defaultImages, ProductImage} from "./product/productImage";
import {AddToCart} from "../../../cart/modules/pages/cart/addToCart";
import {useParams} from "react-router-dom";
import {FavoriteBtn} from "../../../productFavorites/modules/commons/favoriteBtn";
import {useTranslation} from "react-i18next";
import {Loading} from "../../../general/features/loading";
import NotFoundPage from "../../../content/modules/pages/404Page";
import {PriceRequestModal} from "./product/priceRequestModal";
import {ProductQuoteDataInfoBtnModal} from "../features/product/ProductQuoteDataInfoModal";

export function ProductPage() {
    const dispatch = useAppDispatch();
    const {id} = useParams();
    const {t} = useTranslation('common');

    useEffect(() => {
        if (id) {
            dispatch(getProduct(id));
        }
    }, [id]);

    const {product, loading, loaded, upcoming_price_mutations} = useAppSelector((state) => state.product);

    const {currentUser} = useAppSelector((state) => state.currentUser);
    const formatNum = (number: string) => {
        return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(
            parseFloat(number),
        );
    }
    const replacements: { [index: string]: string } = {};
    if (!loading) {
        replacements[id as string] = product?.name ?? t('Product');
    }

    const isWholesale = ['GROOTH', 'GH-INT'].includes(`${currentUser?.data.active_relation?.pricelist}`);

    const [open, setOpen] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const variationOptions = product?.variation_options ?? [];
    const [selectedVariationId, setSelectedVariationId] = useState<number>(0);
    const defaultVariationId = product?.variations?.drupal_internal__variation_id ?? 0;
    const availableVariationOptions = variationOptions.filter(option => {
        if (option.disabled) {
            return false;
        }
        return option.max_quantity === undefined || option.max_quantity > 0;
    });
    const selectedOption = availableVariationOptions.find(option => option.drupal_internal__variation_id === selectedVariationId)
        ?? availableVariationOptions.find(option => option.drupal_internal__variation_id === defaultVariationId)
        ?? availableVariationOptions[0]
        ?? variationOptions.find(option => !option.disabled)
        ?? variationOptions[0];
    const regularOption = availableVariationOptions.find(option => !option.is_limited_stock);
    const actionOption = availableVariationOptions.find(option => option.is_limited_stock);
    const showActionSwitch = !!regularOption && !!actionOption;
    const selectedScaledPrices = selectedOption?.scaled_prices ?? product?.scaled_prices ?? [];
    const hasSelectedThtOption = !!(selectedOption?.is_limited_stock && selectedOption.max_quantity !== undefined && selectedOption.max_quantity > 0);
    const showActionBadge = !!(product.action_active && product.action_label && (product.action_type !== 'tht' || hasSelectedThtOption));
    const disabled = ((selectedScaledPrices.length ?? 0) === 0 && isWholesale) || selectedOption?.disabled;

    useEffect(() => {
        if (defaultVariationId > 0) {
            setSelectedVariationId(defaultVariationId);
        }
    }, [defaultVariationId]);

    useEffect(() => {
        if (!selectedOption) {
            return;
        }
        if (selectedOption.drupal_internal__variation_id !== selectedVariationId) {
            setSelectedVariationId(selectedOption.drupal_internal__variation_id);
        }
    }, [selectedOption?.drupal_internal__variation_id, selectedVariationId]);

    const minQuantity = selectedScaledPrices.length > 0 ? Math.min(...selectedScaledPrices.map(price => price.quantity ?? 1)) : 1;

    const upcomingPrice = Object.values(upcoming_price_mutations).find(m => parseInt(m.product) === parseInt(`${product.cb_product_id}`));

    return (
        <DefaultPage className={"product-page mt-5"} replacements={replacements}>
            {(loaded && Object.keys(product).length === 0) ? <NotFoundPage/> : (
                <div className={'mh-500 p-rel'}>
                    <Row>
                        <Col
                            xs={{order: 1, span: 12}}
                            sm={{order: 0, span: 6}}
                            md={{order: 0, span: 6}}
                            className={'p-rel mh-500'}
                        >
                            {loading ? <><Loading/><ProductImage images={defaultImages}/></> :
                                <ProductImage images={product?.images ?? []}/>}
                        </Col>
                        <Col
                            xs={{order: 0, span: 12}}
                            sm={{order: 1, span: 6}}
                            md={{order: 1, span: 6}}
                        >
                            <PageTitle title={product?.name ?? ""}/>
                            <div className={'p-rel mh-150'}>
                                {loading ? <Loading/> : <>
                                    {showActionBadge && (
                                        <div className={'mb-2'}>
                                            <Badge className={'product-action-badge-primary'}>{product.action_label}</Badge>
                                        </div>
                                    )}
                                    <div className={'upcoming-price'}>
                                        {upcomingPrice && <p>
                                            {t('@percentage% @op from @date').replace('@date', upcomingPrice.mutation.date).replace('@percentage', upcomingPrice.mutation.percentage).replace('@op', upcomingPrice.mutation.oldPrice < upcomingPrice.mutation.newPrice ? t('more expensive') : t('cheaper'))}
                                        </p>}
                                    </div>
                                    <div className={'product-price-badge'}>
                                        {selectedScaledPrices.length > 0 ? selectedScaledPrices.map((item, key) =>
                                            <Badge key={key} bg={'primary'} onClick={() => setQuantity(item.quantity > 0 ? item.quantity : 1)}>
                                                {formatNum(`${item.price ?? ''}`)}
                                                {item.quantity > 0 && <small>{item.quantity} st.</small>}
                                            </Badge>
                                        ) : (
                                          isWholesale ? (
                                              <Badge bg={'primary'} className={'cursor-pointer'} onClick={() => setOpen(true)}>{t('Price on request')}</Badge>
                                          ) : (
                                              <Badge bg={'primary'}>{product.price ?? ''}</Badge>
                                          )
                                        )}
                                    </div>
                                    <div className={'d-flex gap-10'}>
                                        <div className={"d-inline-flex"}>
                                            <AddToCart variationId={selectedOption?.drupal_internal__variation_id ?? product?.variations?.drupal_internal__variation_id} minQuantity={minQuantity} onPriceRequest={() => setOpen(true)}
                                                       variant={'product'} btnDisabled={disabled} quantity={quantity} updateQuantity={q => setQuantity(q)} maxQuantity={selectedOption?.max_quantity}/>
                                        </div>
                                        <FavoriteBtn variant={'product'} size={'25px'} productId={product.id}/>
                                        <ProductQuoteDataInfoBtnModal product={product} />
                                    </div>
                                    {selectedOption?.is_limited_stock && selectedOption.max_quantity !== undefined && (
                                        <div className={'mt-2'}>
                                            <Badge className={'product-action-badge-secondary'}>
                                                {t('Limited stock')}: {selectedOption.max_quantity} {t('available')}
                                            </Badge>
                                        </div>
                                    )}
                                    {showActionSwitch && (
                                        <div className={'action-variation-switch mt-3'}>
                                            <Form.Check
                                                type={'switch'}
                                                id={`action-variation-${product.id}`}
                                                label={t('Use THT action')}
                                                checked={selectedOption?.id === actionOption.id}
                                                onChange={(event) => {
                                                    setSelectedVariationId(
                                                        event.currentTarget.checked
                                                            ? actionOption.drupal_internal__variation_id
                                                            : regularOption.drupal_internal__variation_id
                                                    );
                                                    setQuantity(1);
                                                }}
                                            />
                                        </div>
                                    )}
                                </>}
                            </div>
                        </Col>
                    </Row>
                    <Row className={"mt-5"}>
                        <Col className={'p-rel mh-50'}>
                            {loading ? <Loading/> : <ProductTabs product={product}/>}
                        </Col>
                    </Row>
                    <PriceRequestModal productName={product?.name ?? t('Product')} open={open} onClose={() => setOpen(false)} count={quantity} productId={product.drupal_internal__product_id} minCount={minQuantity} />
                </div>
            )}
        </DefaultPage>
    );
}
